import { ApolloCache, makeVar } from '@apollo/client';

/**
 * Bumped on every finance-list eviction. The useAll* drains (and the ids-only
 * counter) subscribe and re-run their page-walk when it changes — that is
 * what repaints the drains that are MOUNTED at mutation time (HeaderBar,
 * AlertsPanel, open invoice blocks, the registers). Eviction alone only fixes
 * the next mount; client.query walks are not watched queries, so nothing else
 * would re-run them.
 */
export const financeListsVersion = makeVar(0);

/**
 * Invalidate every cached page of the finance list drains (finance-block
 * audit, spec 0008 hotfix round).
 *
 * The useAll* drains page-walk `fakturowniaInvoices`/`expenses` with
 * cache-first over per-(filters, page) entries. A mutation's refetchQueries
 * can only converge the combos it knows about (usually the active page's) —
 * every OTHER previously-visited combo (other date preset, other group/
 * student card, the reconcile chip) would keep serving its stale pages for
 * the rest of the session: ghost rows after deletes, missing rows after
 * creates, already-linked invoices stuck in the «Очередь сверки».
 *
 * Evicting the ROOT_QUERY fields wipes ALL combos at once; the next
 * cache-first walk over any of them misses and goes to the network. Mounted
 * screens still repaint through their own refetch()/refetchQueries — this
 * covers the not-currently-mounted surfaces.
 */
export const evictFinanceLists = (
  cache: ApolloCache<unknown>,
  fields: Array<'fakturowniaInvoices' | 'expenses'> = [
    'fakturowniaInvoices',
    'expenses',
  ],
): void => {
  fields.forEach((fieldName) => {
    cache.evict({ id: 'ROOT_QUERY', fieldName });
  });
  cache.gc();
  financeListsVersion(financeListsVersion() + 1);
};

/** `update` callback for invoice-affecting mutations (linking, regrouping). */
export const evictInvoiceLists = (cache: ApolloCache<unknown>): void =>
  evictFinanceLists(cache, ['fakturowniaInvoices']);

/** `update` callback for expense mutations (create/update/delete/status). */
export const evictExpenseLists = (cache: ApolloCache<unknown>): void =>
  evictFinanceLists(cache, ['expenses']);
